import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import ChipButton from '@/ui-components/ChipButton.vue';
import FilterButtonSeo from '@/components/filter-bar/seo/FilterButtonSeo.vue';
import TitleListSortingSwitcherSeo from '@/components/filter-bar/seo/TitleListSortingSwitcherSeo.vue';
import { CollectionType } from '@/enums/collection-type';
import { FilterValues } from '@/helpers/filter-helper';
import { FilterCollection } from '@/stores/filter.store';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PopularTabType } from '@/interfaces/titles';
import Provider from '@/interfaces/provider';
import ResetButton from '@/components/buttons/ResetButton.vue';
import { getSelectedTab } from './helper';
import TitlesCount from '@/components/filter-bar/TitlesCount.vue';
import { useConstantStore, useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { activeSubgenreCountries } from '@/constants/subgenres';


export default /*#__PURE__*/_defineComponent({
  __name: 'PopularFilterBar',
  props: {
    titleCount: null,
    currentFilters: null,
    selectedTab: null,
    provider: null,
    searchText: null,
    isProviderPage: { type: Boolean, default: false },
    popularPageExpActive: { type: Boolean, default: false },
    activeSubgenreTitle: null
  },
  emits: ["search"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'search', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titleCount: number, currentFilters: FilterCollection, selectedTab?: PopularTabType, provider: Provider, searchText: string, isProviderPage: boolean, popularPageExpActive: boolean, activeSubgenreTitle?: string };

const FilterBarSeo = () =>
	import(/* webpackChunkName: "filter-bar-seo" */ '@/components/filter-bar/seo/FilterBarSeo.vue');

const filterValues = ref({ ...FilterValues }); // Initially clone the FilterValues object

const collectionType = ref(CollectionType);
const showFilterBar = ref(false);
const searchBarVisible = ref(false);

const searchbar = ref<HTMLIonSearchbarElement | null>(null);





const { activeRoute } = useRoutingStore();
const { t } = useI18n();
const { ageCertifications } = useConstantStore();
const { country } = useLanguageStore();

watch(
	() => activeRoute.value,
	(newValue, oldValue) => {
		if (
			newValue.name?.startsWith('app.titles.popular') &&
			oldValue.name?.startsWith('app.titles.popular') &&
			newValue.name !== oldValue.name
		) {
			searchBarVisible.value = false;
			emit('search', '');
		}
	}
);

const isSearchBarVisible = computed(() => {
	return searchBarVisible.value || !!props.searchText;
});

watch(
	() => ageCertifications.value.hasCertifications,
	newValue => {
		if (!newValue) {
			// Remove the age certification filter dynamically if certifications are not available
			delete filterValues.value['FilterAgeCertification'];
		}
	}
);

const activeFiltersCount = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { providers, content_types, sort_by, sorting_random_seed, sort_asc, ...filters } = props.currentFilters;
	return Object.keys(filters).filter((key, index, arr) => {
		const value = filters[key as keyof typeof filters];
		if (
			key.startsWith('release_year') &&
			!!value &&
			index !== arr.findIndex(k => k.startsWith('release_year') && !!filters[k as keyof typeof filters])
		) {
			return false;
		}
		return Array.isArray(value) ? value.length > 0 : !!value;
	}).length;
});

const tabTitle = computed(() => {
	// POPULAR_EXP
	if (!props.provider) {
		if (props.selectedTab == PopularTabType.MOVIE) {
			return 'All movies streaming online';
		} else if (props.selectedTab == PopularTabType.SHOW) {
			return 'All TV shows streaming online';
		} else {
			return '';
		}
	}

	if (
		activeSubgenreCountries.includes(country.value) &&
		props.activeSubgenreTitle &&
		props.currentFilters.subgenres?.length === 1 &&
		props.currentFilters.providers?.length === 1
	) {
		if (props.selectedTab == PopularTabType.MOVIE) {
			return t('WEBAPP_SUBGENRE_MOVIES_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		} else if (props.selectedTab == PopularTabType.SHOW) {
			return t('WEBAPP_SUBGENRE_SHOWS_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		} else {
			return t('WEBAPP_SUBGENRE_ALL_ON_PROVIDER', {
				provider: props.provider.clearName,
				subgenre: props.activeSubgenreTitle,
			});
		}
	}
	// POPULAR_EXP
	switch (props.selectedTab) {
		case PopularTabType.MOVIE:
			return t('WEBAPP_ALL_MOVIES_PROVIDER', { provider: props.provider.clearName });
		case PopularTabType.SHOW:
			return t('WEBAPP_ALL_SHOWS_PROVIDER', { provider: props.provider.clearName });
		default:
			return t('WEBAPP_ALL_MOVIES_SHOWS_PROVIDER', { provider: props.provider.clearName });
	}
});

function change(evt: any) {
	emit('search', evt.target.value);
}

function clear() {
	TrackingHelper.trackEvent('search_suggest_clear', {
		action: 'clear_all',
		property: props.selectedTab,
	});
}

function cancel() {
	setTimeout(() => {
		if (props.searchText === '') {
			searchBarVisible.value = false;
		}
	}, 100);
}

function showSearchBar() {
	searchBarVisible.value = true;
	const selectedTab = getSelectedTab(activeRoute?.value.name as string);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'open_suggester',
		property: selectedTab,
	});

	setTimeout(() => {
		searchbar.value?.setFocus();
	}, 100);
}

function toggleFilterBar() {
	showFilterBar.value = !showFilterBar.value;

	const selectedTab = getSelectedTab(activeRoute?.value.name as string);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'filters_expanded',
		label: 'all',
		property: selectedTab,
	});
}

return { __sfc: true,FilterBarSeo, filterValues, collectionType, showFilterBar, searchBarVisible, searchbar, props, emit, activeRoute, t, ageCertifications, country, isSearchBarVisible, activeFiltersCount, tabTitle, change, clear, cancel, showSearchBar, toggleFilterBar, BasicButton, ChipButton, FilterButtonSeo, TitleListSortingSwitcherSeo, ResetButton, TitlesCount }
}

})